/**
 * AiLoadingOverlay
 *
 * Drop this inside any `relative`-positioned dialog/panel container.
 * When visible it centres a branded spinner + optional message over the content.
 *
 * Usage:
 *   <div className="relative ...">
 *     {content}
 *     <AiLoadingOverlay visible={isRunning} message="Generating rewrite plan…" />
 *   </div>
 */

import React from 'react';
import { Loader2, Sparkles } from 'lucide-react';

export function AiLoadingOverlay({
  visible = false,
  message = 'AI is thinking…',
  /** Blur the content behind. Set false for panels that don't need it. */
  blur = true,
}) {
  if (!visible) return null;

  return (
    <div
      className={`absolute inset-0 z-50 flex flex-col items-center justify-center gap-3 rounded-[inherit]
                  bg-background/75 ${blur ? 'backdrop-blur-[3px]' : ''}`}
      aria-live="polite"
      aria-label={message}
      data-testid="ai-loading-overlay"
    >
      {/* Spinning ring + sparkle badge */}
      <div className="relative">
        <Loader2 className="h-10 w-10 animate-spin text-primary" strokeWidth={1.5} />
        <Sparkles
          className="absolute -top-1 -right-1 h-4 w-4 animate-pulse text-amber-400"
          strokeWidth={1.5}
        />
      </div>

      {/* Message */}
      {message && (
        <p className="max-w-[220px] text-center text-[12.5px] leading-snug text-muted-foreground">
          {message}
        </p>
      )}
    </div>
  );
}

export default AiLoadingOverlay;
